// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityMessageType,
    CartLineModel,
    ProductSubscriptionDto,
} from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import SignInButton from "@werner/App/Components/Common/SignInButton";
import MoreInfoButton from "@werner/App/Components/MoreInfoButton/MoreInfoButton";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import { getIsLeadCapture } from "@werner/App/Store/Selectors/ProductSelectors";
import { ButtonIcon } from "@werner/MobiusOverrides/Button/Props";
import ProductAddedToCartMessage from "@werner/Overrides/Components/ProductAddedToCartMessage";
import { addToCartStatus } from "@werner/XDL/constants";
import Cart from "@werner/XDL/Icons/CartHeader";
import Success2 from "@werner/XDL/Icons/Success2";
import { color, mergeWithCss } from "@werner/XDL/Utilities";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    // XNG-Changes
    iconOnly?: boolean;
    showDisabled?: boolean;
}
// XNG-Changes
export interface IconConfig {
    src: React.ComponentType | string;
    position: "right" | "left";
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);
    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentLocation: getLocation(state),
        // XNG-Changes
        isLeadCaputre: getIsLeadCapture(props.productContext.product),
        isAuthenticated: xIsAuthenticated(state),
        isDiscontinued: props.productContext.product.isDiscontinued,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {
    mergeCss: true,
    buttonType: "solid",
    variant: "primary",
    typographyProps: {
        size: 16,
        weight: 500,
        css: css`
            letter-spacing: 0;
            line-height: 18px;
        `,
    },
    icon: {
        src: Cart,
        position: "right",
        ...{ color: "secondary", size: 26 },
    },
    hoverAnimation: "shoppingCart" as any,
    css: css`
        position: relative;

        &.success > ${TypographyStyle} {
            color: ${color("common.white")};
            text-align: center;
        }
        & .center {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%);
        }
    `,
};

export const productAddToCartIconButtonStyles: ButtonPresentationProps = {
    ...mergeWithCss(productAddToCartButtonStyles, {
        icon: {
            src: Cart,
            position: "right",
            size: 26,
            ...{ color: "secondary" },
            css: css`
                margin: 0;
            `,
        },
        css: css`
            padding: 0 16px;
            width: 60px;
            min-width: 60px;
            justify-content: center;
            &.loading,
            &.success {
                padding: 0 16px;
            }
            ${ButtonIcon} {
                margin-left: 0;
            }
        `,
    }),
};

// XNG-Changes
const dotsStyleColor = "common.white";
const dotsStyle = {
    css: css`
        position: relative;
        left: -9999px;
        width: 3px;
        height: 3px;
        border-radius: 5px;
        background-color: ${color(dotsStyleColor)};
        color: ${color(dotsStyleColor)};
        box-shadow: 9999px 0 0 -5px ${color(dotsStyleColor)};
        animation: dotPulse 1.5s infinite linear;
        animation-delay: 0.25s;

        &::before,
        &::after {
            content: "";
            display: inline-block;
            position: absolute;
            top: 0;
            width: 3px;
            height: 3px;
            border-radius: 5px;
            background-color: ${color(dotsStyleColor)};
            color: ${color(dotsStyleColor)};
        }

        &::before {
            box-shadow: 9984px 0 0 -5px ${color(dotsStyleColor)};
            animation: dotPulseBefore 1.5s infinite linear;
            animation-delay: 0s;
        }

        &::after {
            box-shadow: 10014px 0 0 -5px ${color(dotsStyleColor)};
            animation: dotPulseAfter 1.5s infinite linear;
            animation-delay: 0.5s;
        }

        @keyframes dotPulseBefore {
            0% {
                box-shadow: 9984px 0 0 -5px ${color(dotsStyleColor)};
            }
            30% {
                box-shadow: 9984px 0 0 2px ${color(dotsStyleColor)};
            }
            60%,
            100% {
                box-shadow: 9984px 0 0 -5px ${color(dotsStyleColor)};
            }
        }

        @keyframes dotPulse {
            0% {
                box-shadow: 9999px 0 0 -5px ${color(dotsStyleColor)};
            }
            30% {
                box-shadow: 9999px 0 0 2px ${color(dotsStyleColor)};
            }
            60%,
            100% {
                box-shadow: 9999px 0 0 -5px ${color(dotsStyleColor)};
            }
        }

        @keyframes dotPulseAfter {
            0% {
                box-shadow: 10014px 0 0 -5px ${color(dotsStyleColor)};
            }
            30% {
                box-shadow: 10014px 0 0 2px ${color(dotsStyleColor)};
            }
            60%,
            100% {
                box-shadow: 10014px 0 0 -5px ${color(dotsStyleColor)};
            }
        }
    `,
};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory, inventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    iconOnly,
    signInUrl,
    currentLocation,
    showDisabled,
    isLeadCaputre,
    isAuthenticated,
    isDiscontinued,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeWithCss(productAddToCartButtonStyles, extendedStyles));
    const [btnIconStyles] = useState(() => mergeWithCss(productAddToCartIconButtonStyles, extendedStyles));
    const [status, setStatus] = useState(addToCartStatus.default);
    const history = useHistory();

    // XNG-Changes
    const iconConfig: IconConfig = {
        src: status === addToCartStatus.success ? Success2 : Cart,
        position: "right",
        ...{ color: "secondary", size: 26 },
    };
    // XNG-Changes
    useEffect(() => {
        if (status === addToCartStatus.success) {
            setTimeout(() => {
                setStatus(addToCartStatus.default);
            }, 800);
        }
    }, [status]);

    const redirectToSignIn = () => {
        if (!signInUrl) {
            logger.warn("No url was found for SignInPage, defaulting to /SignIn");
            history.push("/MyAccount/SignIn");
        } else {
            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (returnUrl === "/") {
                history.push(`${signInUrl}`);
            } else {
                history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}`);
            }
        }
    };

    // <XNG-Changes>
    if (!isAuthenticated) {
        return <SignInButton />;
    }
    if (isAuthenticated && isLeadCaputre) {
        return <MoreInfoButton />;
    }
    // </XNG-Changes>

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart || isDiscontinued) {
        return null;
    }

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();
        let qtyExceededInventory = false;
        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        // XNG-Changes
        setStatus(addToCartStatus.loading);

        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        // XNG-Changes
        setStatus(addToCartStatus.success);

        if (inventory) {
            qtyExceededInventory = qtyOrdered > inventory.qtyOnHand;
        }

        if (showAddToCartConfirmationDialog) {
            const availability =
                inventory?.inventoryAvailabilityDtos?.find(
                    o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
                )?.availability || undefined;
            const isNonStock = !availability || availability.messageType === AvailabilityMessageType.OutOfStock;
            toasterContext.addToast({
                body: (
                    <ProductAddedToCartMessage
                        isQtyAdjusted={cartline.isQtyAdjusted}
                        multipleProducts={false}
                        qtyExceededInventory={qtyExceededInventory}
                        isNonStock={isNonStock}
                    />
                ),
                messageType: "success",
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
            });
        }
    };

    /**
     * XNG-Changes
     * to prevent button width change when the status change, we use position absolute and text-color: transparent
     */
    if (iconOnly) {
        return (
            <Button
                {...btnIconStyles}
                onClick={addToCartClickHandler}
                className={`button-wrapper-icon-only ${status.status}`}
                disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
                icon={iconConfig}
                {...otherProps}
                aria-label="add to cart icon button"
                // {...{ isLoading: addToCartStatus.loading }}
            >
                {/* {status === addToCartStatus.loading ? (
                    <StyledWrapper className="center">
                        <StyledWrapper {...dotsStyle} />
                    </StyledWrapper>
                ) : (
                    <Icon src={Cart} size={20} color="inherit" />
                )} */}
            </Button>
        );
    }
    // XNG-Changes
    return (
        <Button
            {...styles}
            onClick={addToCartClickHandler}
            // XNg-Changes: add status !== addToCartStatus.default
            disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
            className={`${status.status}`}
            icon={iconConfig}
            {...otherProps}
            aria-label="add to cart button"
            {...{ isLoading: status === addToCartStatus.loading }}
        >
            <Typography {...productAddToCartButtonStyles.typographyProps}>
                {labelOverride ?? translate(addToCartStatus.default.label || "")}
            </Typography>
        </Button>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
